import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  TruckIcon,
  CalendarDaysIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import { getApiFieldName } from '../../utils/dynamicColumns';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import { OrderDispatcherFilter } from '../../components/filters';
import { Drawer } from '../../components/ui/Drawer';
import FilterBar from '../../components/FilterBar';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import ordersService from '../../services/orders.service';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import styles from './StreetTurnOpportunities.module.css';

// Interface for street turn opportunity data from API
interface StreetTurnOpportunity {
  recno: number;
  street_turn_date: string;
  import_pronumber: number;
  export_pronumber: number;
  avail_start_datetime: string;
  avail_end_datetime: string;
  est_distance: number;
  // Import order details
  import_legnum: number;
  import_origin: string;
  import_dest: string;
  import_origin_city_state: string;
  import_dest_city_state: string;
  import_container_size: string;
  import_chassis_size: string;
  import_chassis_provider: string;
  import_status: string;
  import_driver: string;
  isMule?: boolean;
  import_legdrop: string;
  import_dest_arrival: string;
  import_dispatcher: string;
  import_region: string;
  import_scac: string;
  import_equiptype: string;
  import_drop_release: string;
  import_perdiem_date: string;
  import_customer: string;
  import_current_location: string;
  import_current_location_city_state: string;
  // Export order details
  export_legnum: number;
  export_origin: string;
  export_dest: string;
  export_origin_city_state: string;
  export_dest_city_state: string;
  export_container_size: string;
  export_chassis_size: string;
  export_chassis_provider: string;
  export_orig_appt_date: string;
  export_dest_appt_date: string;
  export_appt_time: string;
  export_dispatcher: string;
  export_region: string;
  export_scac: string;
  export_equiptype: string;
  export_cutoff: string;
  export_customer: string;
}

// Grouped row for the table (without storing matches to reduce memory)
interface GroupedRow {
  id: string;
  pronumber: number;
  location: string; // Current location for imports, pickup dest for exports
  customer: string;
  scac: string;
  container_size: string;
  equiptype: string;
  chassis_size: string;
  chassis_provider: string;
  release_date: string; // Drop release for imports, cutoff for exports
  perdiem_date: string; // Per diem start date (imports only)
  delivery_appt_date: string; // Delivery appointment date (exports only)
  status: string;
  driver: string;
  isMule?: boolean;
  legdrop: string; // Leg drop status (imports only)
  matchCount: number;
  // Added order columns ride along too, copied from the group's first match — see
  // `attachedKeys`. Not declared: their names are chosen at runtime from the field
  // registry, so there is nothing to type them as.
}

type ViewMode = 'import' | 'export';

// Auto-refresh interval (15 seconds)
const REFRESH_INTERVAL = 15000;

// Format date/datetime for display (outside component to avoid recreation)
const formatDate = (dateStr: string): string => {
  if (!dateStr || dateStr === '0000-00-00' || dateStr === '0000-00-00 00:00:00') return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
};

function StreetTurnOpportunitiesContent() {
  const { isViewed, markViewed } = useViewedPros();
  // Data state
  const [data, setData] = useState<StreetTurnOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // View mode toggle
  const [viewMode, setViewMode] = useState<ViewMode>('import');

  // Any orders-scope registry field can be added as a column. A row pairs an IMPORT
  // order with an EXPORT one, so there is no single pro to key on — the active view
  // decides which side the columns come from, and switching it refetches. The
  // selection is keyed per view for the same reason the layout already is.
  const fieldSelection = useAdditionalFieldSelection(`street-turn-opportunities-${viewMode}`);
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  // The keys the backend attaches, which is what a generated column reads. This grid
  // does not hand TanTable the API rows: it GROUPS them into GroupedRow objects built
  // from an explicit field list, so anything not named there is dropped on the floor.
  const attachedKeys = useMemo(
    () => fieldSelection.additionalFields.map(getApiFieldName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [additionalFieldsKey],
  );

  // Global filters
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers,
    setSelectedOrderDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Local filters
  const [selectedContainerSizes, setSelectedContainerSizes] = useState<string[]>([]);
  const [selectedChassisSizes, setSelectedChassisSizes] = useState<string[]>([]);
  const [selectedScacs, setSelectedScacs] = useState<string[]>([]);
  const [selectedEquipTypes, setSelectedEquipTypes] = useState<string[]>([]);
  const localFiltersLoaded = useRef(false);
  const isMounted = useRef(true);

  // Region options
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);

  // Fetch region options on mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionsRes = await ordersService.getRegions();
        setRegionOptions(
          regionsRes.data.regions.map((region: string) => ({
            value: region,
            label: region
          }))
        );
      } catch (error) {
        console.error('Failed to fetch regions:', error);
      }
    };
    fetchRegions();
  }, []);

  // Matches drawer
  const [selectedGroupRow, setSelectedGroupRow] = useState<GroupedRow | null>(null);
  const [matchesDrawerOpen, setMatchesDrawerOpen] = useState(false);
  const [matchesSortColumn, setMatchesSortColumn] = useState<string>('distance');
  const [matchesSortDesc, setMatchesSortDesc] = useState<boolean>(false);

  // Order details drawer
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('street_turn_opportunities_filters');
        if (!isMounted.current) return;
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (filters.selectedContainerSizes) setSelectedContainerSizes(filters.selectedContainerSizes);
          if (filters.selectedChassisSizes) setSelectedChassisSizes(filters.selectedChassisSizes);
          if (filters.selectedScacs) setSelectedScacs(filters.selectedScacs);
          if (filters.selectedEquipTypes) setSelectedEquipTypes(filters.selectedEquipTypes);
          if (filters.viewMode) setViewMode(filters.viewMode);
        }
      } catch {
        // Silently fail
      }
      if (isMounted.current) {
        localFiltersLoaded.current = true;
      }
    };
    loadLocalFilters();
  }, []);

  // Save local filters (debounced)
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (saveLocalFiltersTimeoutRef.current) {
        clearTimeout(saveLocalFiltersTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!localFiltersLoaded.current) return;

    if (saveLocalFiltersTimeoutRef.current) {
      clearTimeout(saveLocalFiltersTimeoutRef.current);
    }
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('street_turn_opportunities_filters', {
          selectedContainerSizes,
          selectedChassisSizes,
          selectedScacs,
          selectedEquipTypes,
          viewMode,
        });
      } catch {
        // Silently fail
      }
    }, 500);
  }, [selectedContainerSizes, selectedChassisSizes, selectedScacs, selectedEquipTypes, viewMode]);

  // Fetch data
  const fetchData = useCallback(async (showLoading = false) => {
    if (!isMounted.current) return;
    // The saved selection is part of the request; going early just fetches twice.
    if (!fieldSelection.ready) return;

    try {
      setError(null);
      if (showLoading) setApplyingFilters(true);
      const params = new URLSearchParams();
      if (selectedRegions.length > 0) {
        params.append('region', selectedRegions.join(','));
      }
      if (selectedOrderDispatchers.length > 0) {
        params.append('dispatcher', selectedOrderDispatchers.join(','));
      }

      params.append('view', viewMode);
      if (additionalFieldsKey) params.append('additional_fields', additionalFieldsKey);

      const response = await api.get(`/street-turn-opportunities/opportunities?${params.toString()}`);
      if (!isMounted.current) return;
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.error || 'Failed to fetch data');
      }
    } catch (err) {
      if (!isMounted.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      if (isMounted.current) {
        setLoading(false);
        if (showLoading) setApplyingFilters(false);
      }
    }
  }, [selectedRegions, selectedOrderDispatchers, viewMode, fieldSelection.ready, additionalFieldsKey]);

  // Initial fetch on filter change
  useEffect(() => {
    if (globalFiltersLoading) return;
    fetchData(true); // Show loading on filter change
  }, [fetchData, globalFiltersLoading]);

  // Background auto-refresh (silent)
  useVisibilityAwareInterval(() => fetchData(false), globalFiltersLoading ? null : REFRESH_INTERVAL);

  // Filter data based on local filters
  const filteredData = useMemo(() => {
    let result = data;

    // Filter by container size
    if (selectedContainerSizes.length > 0) {
      result = result.filter(
        (row) =>
          selectedContainerSizes.includes(row.import_container_size) ||
          selectedContainerSizes.includes(row.export_container_size)
      );
    }

    // Filter by chassis size
    if (selectedChassisSizes.length > 0) {
      result = result.filter(
        (row) =>
          selectedChassisSizes.includes(row.import_chassis_size) ||
          selectedChassisSizes.includes(row.export_chassis_size)
      );
    }

    // Filter by SCAC
    if (selectedScacs.length > 0) {
      result = result.filter(
        (row) =>
          selectedScacs.includes(row.import_scac) ||
          selectedScacs.includes(row.export_scac)
      );
    }

    // Filter by equipment type
    if (selectedEquipTypes.length > 0) {
      result = result.filter(
        (row) =>
          selectedEquipTypes.includes(row.import_equiptype) ||
          selectedEquipTypes.includes(row.export_equiptype)
      );
    }

    return result;
  }, [data, selectedContainerSizes, selectedChassisSizes, selectedScacs, selectedEquipTypes]);

  // Group data by pronumber based on view mode
  const groupedData = useMemo((): GroupedRow[] => {
    const groups = new Map<number, StreetTurnOpportunity[]>();

    filteredData.forEach((row) => {
      const key = viewMode === 'import' ? row.import_pronumber : row.export_pronumber;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(row);
    });

    return Array.from(groups.entries()).map(([pronumber, matches]) => {
      const firstMatch = matches[0];
      // Every match in a group shares the grouping pro, and the attached columns are
      // keyed on that same pro — so all matches carry identical values and firstMatch
      // is as good as any. Copied by name rather than spreading firstMatch, which
      // would drag ~50 raw import_/export_ keys onto the row as well.
      const attached: Record<string, unknown> = {};
      for (const key of attachedKeys) attached[key] = (firstMatch as any)[key];
      if (viewMode === 'import') {
        // Show driver only if latest leg hasn't arrived at destination yet
        const shouldShowDriver = !firstMatch.import_dest_arrival ||
          firstMatch.import_dest_arrival === '0000-00-00 00:00:00' ||
          firstMatch.import_dest_arrival === '' ||
          firstMatch.import_dest_arrival === null;

        return {
          ...attached,
          id: `import-${pronumber}`,
          pronumber,
          location: firstMatch.import_current_location || firstMatch.import_dest,
          customer: firstMatch.import_customer,
          scac: firstMatch.import_scac,
          container_size: firstMatch.import_container_size,
          equiptype: firstMatch.import_equiptype,
          chassis_size: firstMatch.import_chassis_size,
          chassis_provider: firstMatch.import_chassis_provider,
          release_date: formatDate(firstMatch.import_drop_release),
          perdiem_date: formatDate(firstMatch.import_perdiem_date),
          delivery_appt_date: '-',
          status: firstMatch.import_status,
          driver: shouldShowDriver ? firstMatch.import_driver : '',
          isMule: shouldShowDriver && firstMatch.isMule,
          legdrop: firstMatch.import_legdrop,
          matchCount: matches.length,
        };
      } else {
        // Choose delivery appointment: use orig appt if it exists and is earlier than dest appt
        let deliveryApptDate = firstMatch.export_dest_appt_date;
        if (
          firstMatch.export_orig_appt_date &&
          firstMatch.export_orig_appt_date !== '0000-00-00' &&
          firstMatch.export_orig_appt_date !== '' &&
          firstMatch.export_dest_appt_date &&
          firstMatch.export_dest_appt_date !== '0000-00-00' &&
          firstMatch.export_dest_appt_date !== ''
        ) {
          const origDate = new Date(firstMatch.export_orig_appt_date);
          const destDate = new Date(firstMatch.export_dest_appt_date);
          if (origDate < destDate) {
            deliveryApptDate = firstMatch.export_orig_appt_date;
          }
        }

        return {
          ...attached,
          id: `export-${pronumber}`,
          pronumber,
          location: firstMatch.export_dest, // Pickup destination
          customer: firstMatch.export_customer,
          scac: firstMatch.export_scac,
          container_size: firstMatch.export_container_size,
          equiptype: firstMatch.export_equiptype,
          chassis_size: firstMatch.export_chassis_size,
          chassis_provider: firstMatch.export_chassis_provider,
          release_date: formatDate(firstMatch.export_cutoff),
          perdiem_date: '-',
          delivery_appt_date: formatDate(deliveryApptDate),
          status: '-',
          driver: '-',
          legdrop: '-',
          matchCount: matches.length,
        };
      }
    });
  }, [filteredData, viewMode, attachedKeys]);

  // Get unique values for filter dropdowns
  const containerSizeOptions: MultiSelectOption[] = useMemo(() => {
    const sizes = new Set<string>();
    data.forEach((row) => {
      if (row.import_container_size) sizes.add(row.import_container_size);
      if (row.export_container_size) sizes.add(row.export_container_size);
    });
    return Array.from(sizes)
      .sort()
      .map((size) => ({ value: size, label: size }));
  }, [data]);

  const chassisSizeOptions: MultiSelectOption[] = useMemo(() => {
    const sizes = new Set<string>();
    data.forEach((row) => {
      if (row.import_chassis_size) sizes.add(row.import_chassis_size);
      if (row.export_chassis_size) sizes.add(row.export_chassis_size);
    });
    return Array.from(sizes)
      .sort()
      .map((size) => ({ value: size, label: size }));
  }, [data]);

  const scacOptions: MultiSelectOption[] = useMemo(() => {
    const scacs = new Set<string>();
    data.forEach((row) => {
      if (row.import_scac) scacs.add(row.import_scac);
      if (row.export_scac) scacs.add(row.export_scac);
    });
    return Array.from(scacs)
      .sort()
      .map((scac) => ({ value: scac, label: scac }));
  }, [data]);

  const equipTypeOptions: MultiSelectOption[] = useMemo(() => {
    const types = new Set<string>();
    data.forEach((row) => {
      if (row.import_equiptype) types.add(row.import_equiptype);
      if (row.export_equiptype) types.add(row.export_equiptype);
    });
    return Array.from(types)
      .sort()
      .map((type) => ({ value: type, label: type }));
  }, [data]);

  // Open order details drawer
  const openOrderDrawer = useCallback((pronumber: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  // Close order details drawer
  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // Handle matches count click
  const handleMatchesClick = useCallback((row: GroupedRow, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedGroupRow(row);
    setMatchesDrawerOpen(true);
  }, []);

  // Memoized close handler for matches drawer
  const handleCloseMatches = useCallback(() => setMatchesDrawerOpen(false), []);

  // Stable getRowId function
  const getRowId = useCallback((row: GroupedRow) => row.id, []);

  // Column definitions for grouped view
  const baseColumns: ColumnDef<GroupedRow>[] = useMemo(() => {
    const baseColumns: ColumnDef<GroupedRow>[] = [
      {
        accessorKey: 'pronumber',
        id: 'pronumber',
        header: viewMode === 'import' ? 'Import Pro' : 'Export Pro',
        cell: (info) => {
          const row = info.row.original;
          const viewed = isViewed(row.pronumber);
          return (
            <div
              className={`px-1 rounded transition-colors duration-150 ${
                viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
              }`}
              title={viewed ? 'Viewed' : undefined}
            >
              <button
                className={styles.proLink}
                onClick={(e) => {
                  markViewed(row.pronumber);
                  openOrderDrawer(row.pronumber, e);
                }}
              >
                {row.pronumber}
              </button>
            </div>
          );
        },
        meta: {
          sticky: 'left',
        },
      },
      {
        id: 'location',
        accessorKey: 'location',
        header: viewMode === 'import' ? 'Current Location' : 'Dest',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'customer',
        accessorKey: 'customer',
        header: 'Customer',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'scac',
        accessorKey: 'scac',
        header: 'SCAC',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'container_size',
        accessorKey: 'container_size',
        header: 'Size',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'equiptype',
        accessorKey: 'equiptype',
        header: 'Type',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'chassis_provider',
        accessorKey: 'chassis_provider',
        header: 'Chassis Provider',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'chassis_size',
        accessorKey: 'chassis_size',
        header: 'Chassis Type',
        cell: (info) => info.getValue<string>() || '-',
      },
      {
        id: 'release_date',
        accessorKey: 'release_date',
        header: viewMode === 'import' ? 'Drop Release' : 'Cutoff',
        cell: (info) => info.getValue<string>() || '-',
      },
    ];

    if (viewMode === 'import') {
      baseColumns.push(
        {
          id: 'perdiem_date',
          accessorKey: 'perdiem_date',
          header: 'Per Diem',
          cell: (info) => info.getValue<string>() || '-',
        },
        {
          id: 'driver',
          accessorKey: 'driver',
          header: 'Driver',
          meta: { exportValue: (row: { original: GroupedRow }) => `${row.original.driver || ''}${row.original.isMule ? ' (Mule)' : ''}` },
          cell: (info) => <>{info.getValue<string>() || '-'}<MuleBadge isMule={info.row.original.isMule} /></>,
        }
      );
    } else {
      baseColumns.push({
        id: 'delivery_appt_date',
        accessorKey: 'delivery_appt_date',
        header: 'Delivery Appt',
        cell: (info) => info.getValue<string>() || '-',
      });
    }

    baseColumns.push({
      id: 'matchCount',
      accessorKey: 'matchCount',
      header: viewMode === 'import' ? 'Export Matches' : 'Import Matches',
      cell: (info) => {
        const row = info.row.original;
        const count = info.getValue<number>();
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleMatchesClick(row, e);
            }}
            className="px-2 py-1 bg-info text-info-fg rounded text-sm hover:bg-blue-200 dark:hover:bg-blue-800 transition-colors cursor-pointer"
          >
            {count}
          </button>
        );
      },
      enableGrouping: false,
      meta: {
        sticky: 'right',
      },
    });

    return baseColumns;
  }, [viewMode, openOrderDrawer, handleMatchesClick, isViewed, markViewed]);

  const { columns, availableFields } = useAdditionalFieldColumns<GroupedRow>(
    fieldSelection, { baseColumns },
  );

  // Handle matches table sort
  const handleMatchesSort = useCallback((column: string) => {
    if (matchesSortColumn === column) {
      setMatchesSortDesc(!matchesSortDesc);
    } else {
      setMatchesSortColumn(column);
      setMatchesSortDesc(false);
    }
  }, [matchesSortColumn, matchesSortDesc]);

  // Get matches for the selected row (for drawer)
  const selectedMatches = useMemo(() => {
    if (!selectedGroupRow) return [];
    const isImportView = viewMode === 'import';
    const matches = filteredData.filter((item) =>
      isImportView
        ? item.import_pronumber === selectedGroupRow.pronumber
        : item.export_pronumber === selectedGroupRow.pronumber
    );

    // Sort matches
    return [...matches].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (matchesSortColumn) {
        case 'pro':
          aVal = isImportView ? a.export_pronumber : a.import_pronumber;
          bVal = isImportView ? b.export_pronumber : b.import_pronumber;
          break;
        case 'distance':
          aVal = a.est_distance || 0;
          bVal = b.est_distance || 0;
          break;
        case 'location':
          aVal = isImportView ? (a.export_dest || '') : (a.import_current_location || a.import_dest || '');
          bVal = isImportView ? (b.export_dest || '') : (b.import_current_location || b.import_dest || '');
          break;
        case 'customer':
          aVal = isImportView ? (a.export_customer || '') : (a.import_customer || '');
          bVal = isImportView ? (b.export_customer || '') : (b.import_customer || '');
          break;
        case 'scac':
          aVal = isImportView ? (a.export_scac || '') : (a.import_scac || '');
          bVal = isImportView ? (b.export_scac || '') : (b.import_scac || '');
          break;
        case 'size':
          aVal = isImportView ? (a.export_container_size || '') : (a.import_container_size || '');
          bVal = isImportView ? (b.export_container_size || '') : (b.import_container_size || '');
          break;
        case 'type':
          aVal = isImportView ? (a.export_equiptype || '') : (a.import_equiptype || '');
          bVal = isImportView ? (b.export_equiptype || '') : (b.import_equiptype || '');
          break;
        case 'chassis':
          aVal = isImportView ? (a.export_chassis_size || '') : (a.import_chassis_size || '');
          bVal = isImportView ? (b.export_chassis_size || '') : (b.import_chassis_size || '');
          break;
        case 'date':
          aVal = isImportView ? (a.export_cutoff || '') : (a.import_drop_release || '');
          bVal = isImportView ? (b.export_cutoff || '') : (b.import_drop_release || '');
          break;
        case 'perdiem':
          aVal = a.import_perdiem_date || '';
          bVal = b.import_perdiem_date || '';
          break;
        case 'driver':
          aVal = a.import_driver || '';
          bVal = b.import_driver || '';
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return matchesSortDesc ? bVal - aVal : aVal - bVal;
      }
      const comparison = String(aVal).localeCompare(String(bVal));
      return matchesSortDesc ? -comparison : comparison;
    });
  }, [selectedGroupRow, viewMode, filteredData, matchesSortColumn, matchesSortDesc]);

  return (
    <div className={styles.container}>
      <FilterBar>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'import' ? styles.active : ''}`}
            onClick={() => setViewMode('import')}
          >
            By Import
          </button>
          <button
            className={`${styles.toggleBtn} ${viewMode === 'export' ? styles.active : ''}`}
            onClick={() => setViewMode('export')}
          >
            By Export
          </button>
        </div>
        <FilterBar.Divider />
        <OrderDispatcherFilter
          value={selectedOrderDispatchers}
          onChange={setSelectedOrderDispatchers}
        />
        <FilterBar.Divider />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          size="sm"
        />
        <FilterBar.Divider />
        <MultiSelect
          options={containerSizeOptions}
          value={selectedContainerSizes}
          onChange={setSelectedContainerSizes}
          placeholder="All Sizes"
          showClearOption={true}
          clearOptionLabel="All Sizes"
          size="sm"
        />
        <MultiSelect
          options={equipTypeOptions}
          value={selectedEquipTypes}
          onChange={setSelectedEquipTypes}
          placeholder="All Types"
          showClearOption={true}
          clearOptionLabel="All Types"
          size="sm"
        />
        <MultiSelect
          options={chassisSizeOptions}
          value={selectedChassisSizes}
          onChange={setSelectedChassisSizes}
          placeholder="All Chassis"
          showClearOption={true}
          clearOptionLabel="All Chassis"
          size="sm"
        />
        <MultiSelect
          options={scacOptions}
          value={selectedScacs}
          onChange={setSelectedScacs}
          placeholder="All SCACs"
          showClearOption={true}
          clearOptionLabel="All SCACs"
          size="sm"
        />
      </FilterBar>

      <div className={styles.tableSection}>
        {error ? (
          <div style={{ color: 'red', padding: '1rem' }}>Error: {error}</div>
        ) : (
          <TanTable
            key={viewMode}
            data={groupedData}
            columns={columns}
            availableFields={availableFields}
            additionalFields={fieldSelection.additionalFields}
            onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
            loading={loading || applyingFilters}
            defaultSorting={[{ id: 'matchCount', desc: true }]}
            settingsKey={`street-turn-opportunities-${viewMode}`}
            enableColumnFilters={true}
            enableGrouping={true}
            getRowId={getRowId}
            cardTitle="Street Turn Opportunities"
            fillContainer={true}
            lockedColumns={[
              { id: 'pronumber', side: 'left' },
              { id: 'matchCount', side: 'right' }
            ]}
          />
        )}
      </div>

      <Drawer
        isOpen={matchesDrawerOpen}
        onClose={handleCloseMatches}
        title={selectedGroupRow ? `${viewMode === 'import' ? 'Export' : 'Import'} Matches for Pro ${selectedGroupRow.pronumber}` : 'Matches'}
        position="right"
        width="75vw"
      >
        {selectedGroupRow && (
          <div className="-mx-2">
            {/* Selected Pro Details Summary */}
            <div className="mb-4 px-2 space-y-3">
              {/* Order Information Card */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPinIcon className="h-5 w-5 text-blue-500" />
                  <h3 className="text-sm font-semibold text-body">
                    {viewMode === 'import' ? 'Import' : 'Export'} Order Information
                  </h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-muted mb-1">Pro #</div>
                    <button
                      className={styles.proLink}
                      onClick={(e) => openOrderDrawer(selectedGroupRow.pronumber, e)}
                    >
                      {selectedGroupRow.pronumber}
                    </button>
                  </div>
                  <div>
                    <div className="text-xs text-muted mb-1">
                      {viewMode === 'import' ? 'Current Location' : 'Destination'}
                    </div>
                    <div className="text-sm font-medium text-body">
                      {selectedGroupRow.location || '-'}
                    </div>
                  </div>
                  {viewMode === 'import' && (
                    <div>
                      <div className="text-xs text-muted mb-1">Current Driver</div>
                      <div className="text-sm font-medium text-body">
                        {selectedGroupRow.driver || '-'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Key Dates Card */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarDaysIcon className="h-5 w-5 text-blue-500" />
                  <h3 className="text-sm font-semibold text-body">Key Dates</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-muted mb-1">
                      {viewMode === 'import' ? 'Drop Release' : 'Cutoff'}
                    </div>
                    <div className="text-sm font-medium text-body">
                      {selectedGroupRow.release_date || '-'}
                    </div>
                  </div>
                  {viewMode === 'import' && (
                    <div>
                      <div className="text-xs text-muted mb-1">Per Diem</div>
                      <div className="text-sm font-medium text-body">
                        {selectedGroupRow.perdiem_date || '-'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Equipment Information Card */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TruckIcon className="h-5 w-5 text-blue-500" />
                  <h3 className="text-sm font-semibold text-body">Equipment Information</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-muted mb-1">SCAC</div>
                    <div className="text-sm font-medium text-body">
                      {selectedGroupRow.scac || '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted mb-1">Size/Type</div>
                    <div className="text-sm font-medium text-body">
                      {selectedGroupRow.container_size || '-'} {selectedGroupRow.equiptype || ''}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted mb-1">Chassis Provider</div>
                    <div className="text-sm font-medium text-body">
                      {selectedGroupRow.chassis_provider || '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted mb-1">Chassis Type</div>
                    <div className="text-sm font-medium text-body">
                      {selectedGroupRow.chassis_size || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-xs text-muted mb-2 px-2">
              {selectedMatches.length} match{selectedMatches.length !== 1 ? 'es' : ''} found
            </div>

            <div className={styles.matchesTableWrapper}>
              <table className={styles.matchesTable}>
                <thead>
                  <tr>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('pro')}
                  >
                    Pro # {matchesSortColumn === 'pro' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('distance')}
                  >
                    Distance {matchesSortColumn === 'distance' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('location')}
                  >
                    {viewMode === 'import' ? 'Dest' : 'Current Location'} {matchesSortColumn === 'location' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('customer')}
                  >
                    Customer {matchesSortColumn === 'customer' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('scac')}
                  >
                    SCAC {matchesSortColumn === 'scac' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('size')}
                  >
                    Size {matchesSortColumn === 'size' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('type')}
                  >
                    Type {matchesSortColumn === 'type' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('chassis')}
                  >
                    Chassis {matchesSortColumn === 'chassis' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  <th
                    className="cursor-pointer select-none"
                    onClick={() => handleMatchesSort('date')}
                  >
                    {viewMode === 'import' ? 'Cutoff' : 'Drop Release'} {matchesSortColumn === 'date' && (matchesSortDesc ? '↓' : '↑')}
                  </th>
                  {viewMode === 'export' && (
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleMatchesSort('perdiem')}
                    >
                      Per Diem {matchesSortColumn === 'perdiem' && (matchesSortDesc ? '↓' : '↑')}
                    </th>
                  )}
                  {viewMode === 'export' && (
                    <th
                      className="cursor-pointer select-none"
                      onClick={() => handleMatchesSort('driver')}
                    >
                      Driver {matchesSortColumn === 'driver' && (matchesSortDesc ? '↓' : '↑')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {selectedMatches.map((match, index) => {
                  const isImportView = viewMode === 'import';
                  const matchPro = isImportView ? match.export_pronumber : match.import_pronumber;
                  const matchLocation = isImportView
                    ? match.export_dest
                    : (match.import_current_location || match.import_dest);
                  const matchCustomer = isImportView ? match.export_customer : match.import_customer;
                  const matchScac = isImportView ? match.export_scac : match.import_scac;
                  const matchSize = isImportView ? match.export_container_size : match.import_container_size;
                  const matchType = isImportView ? match.export_equiptype : match.import_equiptype;
                  const matchChassis = isImportView ? match.export_chassis_size : match.import_chassis_size;

                  let dateStr = '-';
                  if (isImportView && match.export_cutoff && match.export_cutoff !== '0000-00-00 00:00:00') {
                    const date = new Date(match.export_cutoff);
                    dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
                  } else if (!isImportView && match.import_drop_release && match.import_drop_release !== '0000-00-00 00:00:00') {
                    const date = new Date(match.import_drop_release);
                    dateStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
                  }

                  let perdiemStr = '-';
                  if (!isImportView && match.import_perdiem_date && match.import_perdiem_date !== '0000-00-00') {
                    const date = new Date(match.import_perdiem_date);
                    perdiemStr = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
                  }

                  return (
                    <tr
                      key={match.recno}
                      className={index % 2 === 1 ? 'zebra-stripe-row' : ''}
                    >
                      <td className="py-1 px-2">
                        <button
                          className={styles.proLink}
                          onClick={(e) => openOrderDrawer(matchPro, e)}
                        >
                          {matchPro}
                        </button>
                      </td>
                      <td className="py-1 px-2">{match.est_distance?.toFixed(1) || '0'} mi</td>
                      <td className="py-1 px-2">{matchLocation || '-'}</td>
                      <td className="py-1 px-2">{matchCustomer || '-'}</td>
                      <td className="py-1 px-2">{matchScac || '-'}</td>
                      <td className="py-1 px-2">{matchSize || '-'}</td>
                      <td className="py-1 px-2">{matchType || '-'}</td>
                      <td className="py-1 px-2">{matchChassis || '-'}</td>
                      <td className="py-1 px-2">{dateStr}</td>
                      {!isImportView && <td className="py-1 px-2">{perdiemStr}</td>}
                      {!isImportView && <td className="py-1 px-2">{match.import_driver || '-'}<MuleBadge isMule={match.isMule} /></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </Drawer>

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

function StreetTurnOpportunities() {
  return (
    <ViewedProsProvider>
      <StreetTurnOpportunitiesContent />
    </ViewedProsProvider>
  );
}

export default StreetTurnOpportunities;
